import { SupplierManualEntry, SupplierSelectionValue, WorkflowCustomField } from '../types';

export const SUPPLIER_SELECTION_FIELD_TYPE = 'supplier_selection';

export const SUPPLIER_FIELD_MARKERS = [
  'fornecedor_sugerido',
  'fornecedores_sugeridos',
  'fornecedor sugerido',
  'fornecedores sugeridos',
];

export const EMPTY_SUPPLIER_SELECTION: SupplierSelectionValue = {
  version: 1,
  selected_supplier_ids: [],
  manual_suppliers: [],
};

export interface SupplierSelectionLimits {
  min: number;
  max: number | null;
  modeLabel: string;
}

export interface SupplierSelectionFieldConfig {
  selection_limit_mode: 'limited' | 'unlimited';
  max_suppliers?: number;
}

const normalizeToken = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const onlyDigits = (value: string): string => value.replace(/\D/g, '');

const parseSupplierSelectionFieldConfig = (
  optionsRaw?: string | null
): SupplierSelectionFieldConfig | null => {
  const text = String(optionsRaw || '').trim();
  if (!text) return null;
  try {
    const parsed = JSON.parse(text);
    if (!parsed || typeof parsed !== 'object') return null;
    const mode = normalizeToken(String((parsed as any).selection_limit_mode || ''));
    if (mode === 'unlimited') {
      return { selection_limit_mode: 'unlimited' };
    }
    if (mode === 'limited') {
      const parsedMax = Number((parsed as any).max_suppliers);
      if (Number.isInteger(parsedMax) && parsedMax >= 1) {
        return { selection_limit_mode: 'limited', max_suppliers: parsedMax };
      }
    }
    return null;
  } catch {
    return null;
  }
};

export const getSupplierSelectionLimits = (
  workflowName?: string | null,
  fieldOptions?: string | null
): SupplierSelectionLimits => {
  const fieldConfig = parseSupplierSelectionFieldConfig(fieldOptions);
  if (fieldConfig?.selection_limit_mode === 'unlimited') {
    return { min: 1, max: null, modeLabel: 'Configuração do campo (sem limite)' };
  }
  if (fieldConfig?.selection_limit_mode === 'limited' && fieldConfig.max_suppliers) {
    return {
      min: 1,
      max: Math.max(1, fieldConfig.max_suppliers),
      modeLabel: 'Configuração do campo (máximo definido)',
    };
  }

  const normalized = normalizeToken(String(workflowName || ''));
  const isQuotationFlow =
    normalized.includes('rfi') ||
    normalized.includes('rfq') ||
    normalized.includes('solicitacao de cotacao');

  if (isQuotationFlow) {
    return { min: 1, max: 3, modeLabel: 'RFI / RFQ / Solicitação de Cotação' };
  }

  return { min: 1, max: 1, modeLabel: 'Demais tipos de fluxo' };
};

export const isSupplierSuggestedField = (field?: WorkflowCustomField | null): boolean => {
  if (!field) return false;
  const candidates = [field.name, field.label, field.group_name || ''];
  return candidates.some((candidate) => {
    const normalized = normalizeToken(String(candidate || ''));
    return SUPPLIER_FIELD_MARKERS.some((marker) => normalized.includes(normalizeToken(marker)));
  });
};

export const isSupplierSelectionFieldType = (
  fieldType?: WorkflowCustomField['field_type'] | string | null
): boolean => normalizeToken(String(fieldType || '')) === SUPPLIER_SELECTION_FIELD_TYPE;

export const isSupplierField = (field?: WorkflowCustomField | null): boolean => {
  if (!field) return false;
  return isSupplierSelectionFieldType(field.field_type) || isSupplierSuggestedField(field);
};

export const applyCnpjMask = (value: string): string => {
  const digits = onlyDigits(value).slice(0, 14);
  if (digits.length <= 2) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  if (digits.length <= 8) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`;
  if (digits.length <= 12) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
};

const formatLocalBrazilianPhone = (digits: string): string => {
  const local = digits.slice(0, 11);
  if (local.length <= 2) return local;
  if (local.length <= 6) return `(${local.slice(0, 2)}) ${local.slice(2)}`;
  if (local.length <= 10) return `(${local.slice(0, 2)}) ${local.slice(2, 6)}-${local.slice(6)}`;
  return `(${local.slice(0, 2)}) ${local.slice(2, 7)}-${local.slice(7)}`;
};

export const applyPhoneMask = (value: string): string => {
  const digits = onlyDigits(value).slice(0, 13);
  // DDI Brasil 55 + DDD + número (até 13 dígitos).
  if (digits.startsWith('55') && digits.length > 2) {
    const rest = digits.slice(2, 13);
    const local = formatLocalBrazilianPhone(rest);
    return local ? `+55 ${local}` : '+55';
  }
  return formatLocalBrazilianPhone(digits);
};

/** Limite do campo telefone no banco (suppliers.telefone VARCHAR(25)). */
export const SUPPLIER_PHONE_MAX_LENGTH = 25;

/**
 * Internacional: sem máscara (não há padrão único de DDI).
 * Nacional: máscara BR. Texto livre no internacional só respeita o tamanho do banco.
 */
export const formatSupplierPhoneInput = (value: string, isInternational: boolean): string => {
  if (isInternational) {
    return String(value || '').slice(0, SUPPLIER_PHONE_MAX_LENGTH);
  }
  return applyPhoneMask(value);
};

/** Exibe telefone: nacional com máscara; internacional como cadastrado. */
export const formatSupplierPhoneDisplay = (
  phone?: string | null,
  isInternational?: boolean,
): string => {
  const raw = String(phone || '').trim();
  if (!raw) return '';
  if (isInternational) return raw.slice(0, SUPPLIER_PHONE_MAX_LENGTH);
  return applyPhoneMask(raw);
};

/**
 * Número local sem DDI: DDD válido (11–99), celular de 11 dígitos iniciando por 9
 * e fixo de 10 dígitos iniciando entre 2 e 8. Sem essa checagem, um número
 * estrangeiro de 10/11 dígitos passaria como nacional.
 */
const isValidBrazilianLocalNumber = (digits: string): boolean => {
  const ddd = Number(digits.slice(0, 2));
  if (!Number.isInteger(ddd) || ddd < 11 || ddd > 99) return false;
  const subscriberFirstDigit = digits.charAt(2);
  if (digits.length === 11) return subscriberFirstDigit === '9';
  return subscriberFirstDigit >= '2' && subscriberFirstDigit <= '8';
};

/** Nacional: 10/11 (DDD+número) ou 12/13 com DDI 55. */
export const isValidBrazilianPhone = (value?: string): boolean => {
  if (!value) return true;
  const digits = onlyDigits(value);
  if (digits.length === 10 || digits.length === 11) return isValidBrazilianLocalNumber(digits);
  if (digits.startsWith('55') && (digits.length === 12 || digits.length === 13)) return true;
  return false;
};

/** Internacional: DDI de outros países (E.164, 8–15 dígitos). */
export const isValidInternationalPhone = (value?: string): boolean => {
  if (!value) return true;
  const digits = onlyDigits(value);
  return digits.length >= 8 && digits.length <= 15;
};

export const isValidEmail = (value: string): boolean => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
};

export const isValidCnpj = (value?: string): boolean => {
  const digits = onlyDigits(String(value || ''));
  if (!digits) return true;
  if (digits.length !== 14) return false;
  if (/^(\d)\1+$/.test(digits)) return false;

  const calcDigit = (base: string) => {
    let factor = base.length - 7;
    let sum = 0;
    for (const char of base) {
      sum += Number(char) * factor;
      factor -= 1;
      if (factor < 2) factor = 9;
    }
    const rest = sum % 11;
    return rest < 2 ? 0 : 11 - rest;
  };

  const digit1 = calcDigit(digits.slice(0, 12));
  const digit2 = calcDigit(digits.slice(0, 12) + String(digit1));
  return digits.endsWith(`${digit1}${digit2}`);
};

const coerceSupplierSelectionFromParsed = (parsed: unknown): SupplierSelectionValue => {
  if (Array.isArray(parsed)) {
    const selectedIds = parsed
      .map((item) => Number(item))
      .filter((item) => Number.isInteger(item) && item > 0);
    return {
      version: 1,
      selected_supplier_ids: Array.from(new Set(selectedIds)),
      manual_suppliers: [],
    };
  }

  if (parsed && typeof parsed === 'object') {
    const payload = parsed as Record<string, unknown>;
    const selectedIds = Array.isArray(payload.selected_supplier_ids)
      ? payload.selected_supplier_ids
          .map((item: unknown) => Number(item))
          .filter((item: number) => Number.isInteger(item) && item > 0)
      : [];
    const manualSuppliers = Array.isArray(payload.manual_suppliers)
      ? payload.manual_suppliers
          .filter((item: unknown) => item && typeof item === 'object')
          .map((item: any) => ({
            corporate_name: String(item.corporate_name || '').trim(),
            contact_name: String(item.contact_name || '').trim(),
            email: String(item.email || '').trim(),
            phone: item.phone ? String(item.phone).trim() : undefined,
            cnpj: item.cnpj ? String(item.cnpj).trim() : undefined,
            created_supplier_id:
              item.created_supplier_id !== undefined && item.created_supplier_id !== null
                ? Number(item.created_supplier_id)
                : undefined,
          }))
          .filter((item: SupplierManualEntry) => item.corporate_name && item.contact_name && item.email)
      : [];

    return {
      version: Number(payload.version) || 1,
      selected_supplier_ids: Array.from(new Set(selectedIds)),
      manual_suppliers: manualSuppliers,
    };
  }

  return { ...EMPTY_SUPPLIER_SELECTION };
};

/** Aceita string JSON ou objeto já parseado (como no load do rascunho). */
export const parseSupplierSelectionValue = (rawValue?: unknown): SupplierSelectionValue => {
  if (rawValue == null || rawValue === '') {
    return { ...EMPTY_SUPPLIER_SELECTION };
  }

  if (typeof rawValue === 'object') {
    return coerceSupplierSelectionFromParsed(rawValue);
  }

  const rawText = String(rawValue).trim();
  if (!rawText || rawText === '[object Object]') {
    return { ...EMPTY_SUPPLIER_SELECTION };
  }

  try {
    return coerceSupplierSelectionFromParsed(JSON.parse(rawText));
  } catch {
    return { ...EMPTY_SUPPLIER_SELECTION };
  }
};

/** Normaliza valor do estado/API para string JSON consumida pelo SupplierFieldControl. */
export const normalizeSupplierFieldRawValue = (rawValue?: unknown): string => {
  if (rawValue == null || rawValue === '') return '';
  if (typeof rawValue === 'string') {
    const trimmed = rawValue.trim();
    if (!trimmed || trimmed === '[object Object]') return '';
    return serializeSupplierSelectionValue(parseSupplierSelectionValue(trimmed));
  }
  if (typeof rawValue === 'object') {
    return serializeSupplierSelectionValue(parseSupplierSelectionValue(rawValue));
  }
  return '';
};

export const getSupplierSelectionCount = (rawValue?: unknown): number => {
  const parsed = parseSupplierSelectionValue(rawValue);
  return (parsed.selected_supplier_ids || []).length;
};

/**
 * Remove IDs que não estão no catálogo ativo (ex.: fornecedor soft-deleted).
 * Não altera manual_suppliers nem a ordem relativa dos IDs mantidos.
 */
export const pruneSupplierSelectionToKnownIds = (
  selection: SupplierSelectionValue,
  knownActiveIds: Iterable<number>
): { selection: SupplierSelectionValue; removedIds: number[] } => {
  const known = new Set(
    Array.from(knownActiveIds).filter((id) => Number.isInteger(id) && id > 0)
  );
  const currentIds = (selection.selected_supplier_ids || []).filter(
    (id) => Number.isInteger(id) && id > 0
  );
  const keptIds: number[] = [];
  const removedIds: number[] = [];
  const seen = new Set<number>();
  for (const id of currentIds) {
    if (seen.has(id)) continue;
    seen.add(id);
    if (known.has(id)) {
      keptIds.push(id);
    } else {
      removedIds.push(id);
    }
  }
  if (removedIds.length === 0 && keptIds.length === currentIds.length) {
    return { selection, removedIds: [] };
  }
  return {
    selection: {
      ...selection,
      version: selection.version || 1,
      selected_supplier_ids: keptIds,
      manual_suppliers: selection.manual_suppliers || [],
    },
    removedIds,
  };
};

export const validateSupplierSelectionValue = (
  rawValue: unknown,
  workflowName?: string | null,
  fieldOptions?: string | null,
  knownActiveIds?: Iterable<number> | null
): string | null => {
  let parsed = parseSupplierSelectionValue(rawValue);
  if (knownActiveIds != null) {
    parsed = pruneSupplierSelectionToKnownIds(parsed, knownActiveIds).selection;
  }
  const count = (parsed.selected_supplier_ids || []).length;
  const limits = getSupplierSelectionLimits(workflowName, fieldOptions);
  const aboveMax = limits.max !== null && count > limits.max;
  if (count < limits.min || aboveMax) {
    if (limits.max === null) {
      return `exige no mínimo ${limits.min} fornecedor(es) para este tipo de fluxo.`;
    }
    return `exige entre ${limits.min} e ${limits.max} fornecedor(es) para este tipo de fluxo.`;
  }
  return null;
};

export const serializeSupplierSelectionValue = (value: SupplierSelectionValue): string => {
  return JSON.stringify(
    {
      version: 1,
      selected_supplier_ids: Array.from(
        new Set((value.selected_supplier_ids || []).filter((id) => Number.isInteger(id) && id > 0))
      ),
      manual_suppliers: (value.manual_suppliers || []).map((entry) => ({
        corporate_name: String(entry.corporate_name || '').trim(),
        contact_name: String(entry.contact_name || '').trim(),
        email: String(entry.email || '').trim(),
        phone: entry.phone ? String(entry.phone).trim() : null,
        cnpj: entry.cnpj ? String(entry.cnpj).trim() : null,
      })),
    },
    null,
    0
  );
};
